import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { ModalDialogComponent } from 'src/app/_common/modal-dialog/modal-dialog.component';
import { EidasUser } from 'src/app/_model/eidasUser';
import { PreviewSpaceResponse, ResponseErrorType } from 'src/app/_model/preview-space-response';
import { EidasService } from 'src/app/_service/eidas-service.service';
import { JwtUtilsService } from 'src/app/_service/jwt-utils.service';
import { ModalDialogService } from 'src/app/_service/modal-dialog.service';
import { PreviewService } from 'src/app/_service/preview.service';
import { StateService } from 'src/app/_service/state.service';
import { Utils } from '../../_common/utils';
import { OotsService } from 'src/app/_service/oots.service';

@Component({
  selector: 'app-eidas-callback',
  templateUrl: './eidas-callback.component.html',
  styleUrls: ['./eidas-callback.component.css']
})
export class EidasCallbackComponent implements OnInit {

  @Input() isPreviewSpace: boolean;
  @Input() uuid: string;
  @Input() session: string;
  @Output() redirect = new EventEmitter<any>();
  continuationToken: string;

  jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;

  constructor(
    private route: ActivatedRoute,
    private jwtUtilService: JwtUtilsService,
    private router: Router,
    private dialogService: ModalDialogService,
    private eidasService: EidasService,
    private cookieService: CookieService,
    private previewService: PreviewService,
    public translate: TranslateService,
    private stateService: StateService,
    private ootsService: OotsService
  ) { }

  currentUser: EidasUser;
  rol: string;
  returnUrl: string;

  ngOnInit(): void {
    this.uuid = this.cookieService.get('uuid') ?? "";
    this.session = window.localStorage.getItem('session') ?? "";
    this.returnUrl = window.localStorage.getItem('returnurl') ?? "";
    this.rol = this.route.snapshot.data['rol'];
    this.isPreviewSpace = this.rol === 'provider' ? true : false;
    this.checkErrorsFromUrl();
    const token = this.route.snapshot.params['id'];

    try {
      if (token && token !== 'undefined') {
        this.currentUser = this.decodeTokenForClaims(token);
        if (!this.isPreviewSpace && Utils.hasValue(this.stateService.continuationToken)) {
          const identifier = this.currentUser.personIdentifier.replace(/[A-Za-z]{2}\/[A-Za-z]{2}\//, "");
          this.ootsService.findPreviousRequestByToken(identifier, this.stateService.continuationToken).subscribe({
            next: (response) => {
              const ootsRequest = JSON.parse(response);
              this.uuid = ootsRequest.uuid;
              this.checkUser(token);
              },
            error: () => this.requestNotFound()
          });
        }
        else this.checkUser(token);
      }
    } catch (error) {
      this.replaceCurrentState();
    }
  }

  private checkErrorsFromUrl(): void {
    if (window.location.href.indexOf("error") > -1) {
      const urlParams = new URLSearchParams(window.location.search);
      const errorType = urlParams.get("errorType");

      let data;
      if (errorType === 'eidas') {
        this.cookieService.set("errorType", JSON.stringify("eidas"), { path: '/' });
        data = {
          title: 'EIDAS.LOGIN_ERROR_TITLE',
          message: 'EIDAS.LOGIN_ERROR_TEXT',
          type: 'error-translate'
        };
      }
      if (errorType === 'petition') {
        this.cookieService.set("errorType", JSON.stringify("petition"), { path: '/' });
        data = {
          title: 'HOME.INCORRECT_PETITION_TITLE',
          message: 'HOME.INCORRECT_PETITION_TEXT',
          type: 'error-translate'
        };
      }
      if (errorType === 'low_loa') {
        data = {
          title: 'EIDAS.LOGIN_ASSURANCE_TITLE',
          message: 'EIDAS.LOGIN_ASSURANCE_TEXT',
          type: 'error-translate'
        };
      }
      this.dialogService.openErrorDialog(ModalDialogComponent, data);
    }
  }

  private checkUser(token: string): void {
    this.eidasService.checkUser(
      this.currentUser,
      this.uuid,
      this.session,
      this.isPreviewSpace
    ).subscribe({
      next: (data) => {
        if (data.status === 'ok') {
          this.jwtUtilService.saveTokenInSessionStorage(token);
          this.jwtUtilService.saveCurrentUser(JSON.stringify(this.currentUser));
          this.jwtUtilService.saveRedirect('true');
          this.replaceCurrentState();
        } else if (data.status === 'no_match') this.handleUserMismatch();
      },
      error: (err) => {
        console.error(err);
        this.replaceCurrentState();
      }
    });
  }

  private requestNotFound(): void {
    const dialog = {
      title: this.translate.instant('HOME.NO_ACTIVE_PROCESS'),
      message: this.translate.instant('HOME.NO_PREVIOUS_PROCESS'),
      type: 'error-translate'
    };
    this.dialogService.openErrorDialog(ModalDialogComponent, dialog);
  }

  private handleUserMismatch(): void {
    const dialog = {
      title: this.translate.instant('EIDAS.LOGIN_CREDENTIAL_MISMATCH_TITLE'),
      message: this.translate.instant('EIDAS.LOGIN_CREDENTIAL_MISMATCH_TEXT'),
      type: this.isPreviewSpace ? 'error-preview' : 'error-translate'
    };

    if (this.isPreviewSpace)
      this.dialogService.openDialog(ModalDialogComponent, dialog).subscribe(result => {
        if (result?.action === 'accept') this.sendUserMismatchError();
      });
    else this.dialogService.openErrorDialog(ModalDialogComponent, dialog);
  }

  private decodeTokenForClaims(token: string): EidasUser {
    const claims = JSON.parse(this.base64TokenDecode(token.split('.')[1]));
    return {
      firstName: claims.firN,
      familyName: claims.famN,
      personIdentifier: claims.sub,
      dateOfBirth: new Date(claims.fNac),
      schemeId: claims.issuer,
      loa: claims.loa
    }
  }

  private base64TokenDecode(str: string) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    const padding = str.length % 4;
    if (padding) {
      str += '='.repeat(4 - padding);
    }
    const binaryData = window.atob(str);
    const utf8Decoder = new TextDecoder('utf-8');
    const bytes = Uint8Array.from(binaryData, c => c.charCodeAt(0));
    return utf8Decoder.decode(bytes);
  }

  sendUserMismatchError() {
    const response = new PreviewSpaceResponse();
    response.errorMessage = "Unable to match logged in user with QueryRequest natural person"
    response.errorType = ResponseErrorType.AUTHENTICATION;
    response.session = this.session;
    this.returnError(response);
  }

  returnError(response: PreviewSpaceResponse) {
    this.previewService.returnError(response).subscribe({
      next: data => {
        window.location.href = this.returnUrl;
      },
      error: errorContent => {
        console.log(errorContent);
      }

    },);
  }

  replaceCurrentState() {
    if (this.isPreviewSpace) this.router.navigate(['/preview-space']);
    else this.router.navigate(['/home']);
  }

}
